import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { Loader2, RefreshCw, Clock, Inbox } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import { MapaCalor, type FilaMapaCalor } from '../../../components/admin/MapaCalor';
import {
  obtenerEstadisticas, type Estadisticas, type FiltrosEstadisticas,
} from '../../../services/correoService';
import { mensajeError } from './errores';
import styles from './CorreoModule.module.css';

/** RF-02 — Estadísticas del servicio con gráficas avanzadas. */
const COLORES = ['#1f8a5b', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b'];

/** Rango por defecto: últimos 30 días (formato yyyy-MM-dd para el filtro por fecha). */
function isoHaceDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

const nf = new Intl.NumberFormat('es-PA');
const pct = (parte: number, total: number) => (total > 0 ? Math.round((parte / total) * 1000) / 10 : 0);

/** Tooltip propio: el de recharts por defecto es una caja gris sin jerarquía. */
function TooltipCorreo({ active, payload, label, sufijo }:
  { active?: boolean; payload?: Array<{ name?: string; value?: number; color?: string }>; label?: string; sufijo?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      {label !== undefined && <div className={styles.tooltipTitulo}>{label}{sufijo}</div>}
      {payload.map((p, i) => (
        <div key={i} className={styles.tooltipFila}>
          <span className={styles.tooltipPunto} style={{ background: p.color }} />
          <span className={styles.tooltipNombre}>{p.name}</span>
          <b className={styles.tooltipValor}>{nf.format(Number(p.value ?? 0))}</b>
        </div>
      ))}
    </div>
  );
}

/**
 * Nombre corto y reconocible de cada aplicación que envía correo.
 *
 * La serie llega con el nombre del registro de aplicación
 * ("pdp-movil-functions"), que no le dice nada a quien lee el panel. El
 * catálogo del servicio trae el nombre de remitente, pero dos aplicaciones
 * comparten "RAISA MINSA", así que no basta para distinguirlas. Estas claves
 * técnicas son estables —son registros de aplicación, no configuración— y lo
 * que no esté aquí cae al nombre de remitente y, en último caso, a la clave.
 */
const APP_ROTULO: Record<string, string> = {
  'pdp-movil-functions': 'App móvil',
  'minsalud-web-functions': 'Portal MINSAlud',
  'gpbot-web-admin': 'Admin gpbot',
  'notif-admin': 'Administración de correo',
};
const rotuloApp = (clave: string, etiqueta?: string) => APP_ROTULO[clave] ?? etiqueta ?? clave;

/** Respaldo si el catálogo no respondió: al menos deja el código legible. */
const nombreCorto = (s: string) => s.replace(/_/g, ' ').toLowerCase()
  .replace(/^./, (c) => c.toUpperCase());

export default function EstadisticasCorreoPage() {
  const { addToast } = useToast();
  const [filtros, setFiltros] = useState<FiltrosEstadisticas>({ desde: isoHaceDias(30), hasta: isoHaceDias(0) });
  const [datos, setDatos] = useState<Estadisticas | null>(null);
  const [cargando, setCargando] = useState(true);

  // Anti-carrera: solo la última carga pedida pinta su resultado (los filtros cambian sin
  // refetch — se recarga con «Actualizar» — pero el botón puede pulsarse en ráfaga).
  const reqId = useRef(0);
  const cargar = useCallback(async () => {
    const id = ++reqId.current;
    setCargando(true);
    try {
      const d = await obtenerEstadisticas(filtros);
      if (id === reqId.current) setDatos(d);
    } catch (err) {
      if (id === reqId.current) addToast('error', mensajeError(err));
    } finally {
      if (id === reqId.current) setCargando(false);
    }
  }, [filtros, addToast]);

  // Carga inicial una sola vez; después se recarga con el botón «Actualizar». Cambiar un
  // filtro NO dispara consulta (evita 400 por códigos de tipo a medio escribir).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const kpis = useMemo(() => {
    if (!datos) return [];
    const i = datos.indicadores;
    const t = datos.tasas;
    return [
      { etiqueta: 'Enviados', valor: nf.format(i.enviados), tono: COLORES[1] },
      { etiqueta: 'Entregados', valor: nf.format(i.entregados), pie: `${t.entrega}% de entrega`, tono: COLORES[0] },
      { etiqueta: 'Aperturas', valor: nf.format(i.aperturasDetectadas), pie: `${t.apertura}% de apertura`, tono: COLORES[4] },
      { etiqueta: 'Clics', valor: nf.format(i.clics), pie: `${t.clic}% de clic`, tono: COLORES[5] },
      { etiqueta: 'Rebotes', valor: nf.format(i.rebotes), pie: `${t.rebote}% de rebote`, tono: COLORES[3] },
      { etiqueta: 'Quejas', valor: nf.format(i.quejas), tono: COLORES[6] },
      { etiqueta: 'Errores', valor: nf.format(i.errores), tono: COLORES[7] },
    ];
  }, [datos]);

  // Embudo enviado → entregado → abierto → clic, con el % respecto a lo enviado.
  const embudo = useMemo(() => {
    if (!datos) return [];
    const i = datos.indicadores;
    const base = i.enviados || 0;
    return [
      { etapa: 'Enviados', total: i.enviados },
      { etapa: 'Entregados', total: i.entregados },
      { etapa: 'Abiertos', total: i.aperturasDetectadas },
      { etapa: 'Clics', total: i.clics },
    ].map((e) => ({ ...e, porcentaje: pct(e.total, base) }));
  }, [datos]);

  const serieDia = useMemo(
    () => (datos?.serieDia ?? []).map((g) => ({
      fecha: g.clave.slice(5),
      enviados: g.indicadores.enviados,
      entregados: g.indicadores.entregados,
      rebotes: g.indicadores.rebotes,
    })),
    [datos],
  );

  /* Distribución por tipo: antes era un pastel con etiquetas y líneas guía. Con
     tipos de participación muy dispar (uno del 45% y varios del 1%) las hojuelas
     quedaban invisibles y las guías se cruzaban entre sí. Ahora es un anillo con
     el total al centro y una lista al lado con cifra y porcentaje — la
     proporción se ve, y el dato exacto se lee. */
  const porTipo = useMemo(() => {
    const filas = (datos?.serieTipo ?? [])
      .map((g) => ({
        nombre: g.etiqueta || nombreCorto(g.clave),
        codigo: g.clave,
        autenticacion: g.esAutenticacion,
        enviados: g.indicadores.enviados,
      }))
      .sort((a, b) => b.enviados - a.enviados);
    const total = filas.reduce((a, f) => a + f.enviados, 0);
    // Tipos configurados en el servicio que NO tuvieron ningún envío en el
    // período. Se listan aparte porque su ausencia es información: revela que
    // hay tipos dados de alta que nadie está usando.
    const usados = new Set(filas.map((f) => f.codigo));
    const sinEnvios = Object.entries(datos?.tiposEtiqueta ?? {})
      .filter(([codigo]) => !usados.has(codigo))
      .map(([codigo, etiqueta]) => ({ codigo, nombre: etiqueta }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return { filas, total, sinEnvios };
  }, [datos]);

  const porApp = useMemo(
    () => (datos?.serieAplicacion ?? [])
      .map((g) => ({
        nombre: rotuloApp(g.clave, g.etiqueta),
        tecnico: g.clave,
        remitente: g.etiqueta,
        enviados: g.indicadores.enviados,
      }))
      .sort((a, b) => b.enviados - a.enviados),
    [datos],
  );

  /* ── Actividad por hora ────────────────────────────────────────────────── */
  const horas = datos?.serieHora ?? null;
  const horasColumnas = useMemo(() => (horas ?? []).map((h) => h.hora), [horas]);
  const hayActividadHoraria = !!horas?.some((h) => h.enviados > 0);

  /** Mapa de calor por tipo de correo × hora — mismo despliegue que el triage. */
  const filasTipoHora: FilaMapaCalor[] = useMemo(() => {
    if (!horas) return [];
    const tipos = new Map<string, number>();
    horas.forEach((h) => Object.entries(h.porTipo).forEach(([t, n]) => tipos.set(t, (tipos.get(t) ?? 0) + n)));
    return [...tipos.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([tipo], i) => ({
        clave: tipo,
        etiqueta: datos?.tiposEtiqueta?.[tipo] ?? nombreCorto(tipo),
        color: COLORES[i % COLORES.length],
        valores: horas.map((h) => h.porTipo[tipo] ?? 0),
      }));
  }, [horas, datos]);

  /** Mapa de calor por desenlace × hora: enviados, entregados, aperturas, reportes. */
  const filasEstadoHora: FilaMapaCalor[] = useMemo(() => {
    if (!horas) return [];
    return [
      { clave: 'enviados', etiqueta: 'Enviados', color: COLORES[1], valores: horas.map((h) => h.enviados) },
      { clave: 'entregados', etiqueta: 'Entregados', color: COLORES[0], valores: horas.map((h) => h.entregados) },
      { clave: 'rebotes', etiqueta: 'Rebotes', color: COLORES[3], valores: horas.map((h) => h.rebotes) },
      { clave: 'errores', etiqueta: 'Errores', color: COLORES[7], valores: horas.map((h) => h.errores) },
    ];
  }, [horas]);

  const serieHoraBarras = useMemo(() => (horas ?? []).map((h) => ({
    hora: h.hora,
    enviados: h.enviados,
  })), [horas]);

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.campo}>
          <label htmlFor="desde">Desde</label>
          <input id="desde" type="date" value={filtros.desde ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="hasta">Hasta</label>
          <input id="hasta" type="date" value={filtros.hasta ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="tipo">Tipo de correo</label>
          <input id="tipo" type="text" placeholder="p. ej. PIN_ACCESO" value={filtros.tipo ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value }))} />
        </div>
        <Button variant="secondary" size="sm" onClick={cargar} disabled={cargando}>
          <RefreshCw size={15} /> Actualizar
        </Button>
      </div>

      {cargando && !datos ? (
        <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>
      ) : !datos ? (
        <div className={styles.vacio}>Sin datos para el filtro seleccionado.</div>
      ) : (
        <>
          <div className={styles.kpis}>
            {kpis.map((k) => (
              <div key={k.etiqueta} className={styles.kpi} style={{ ['--kpi-tono' as string]: k.tono }}>
                <div className={styles.kpiValor}>{k.valor}</div>
                <div className={styles.kpiEtiqueta}>{k.etiqueta}</div>
                {k.pie && <div className={styles.kpiPie}>{k.pie}</div>}
              </div>
            ))}
          </div>

          <div className={styles.graficas}>
            <div className={`${styles.grafica} ${styles.graficaAncha}`}>
              <div className={styles.graficaTitulo}>Evolución diaria</div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={serieDia} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                  <defs>
                    {[['gEnv', COLORES[1]], ['gEnt', COLORES[0]], ['gReb', COLORES[3]]].map(([id, c]) => (
                      <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={c} stopOpacity={0.02} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid stroke="var(--color-border-soft)" vertical={false} />
                  <XAxis dataKey="fecha" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={44} />
                  <Tooltip content={<TooltipCorreo />} cursor={{ stroke: 'var(--color-border)' }} />
                  <Area type="monotone" dataKey="enviados" name="Enviados" stroke={COLORES[1]} strokeWidth={2} fill="url(#gEnv)" />
                  <Area type="monotone" dataKey="entregados" name="Entregados" stroke={COLORES[0]} strokeWidth={2} fill="url(#gEnt)" />
                  <Area type="monotone" dataKey="rebotes" name="Rebotes" stroke={COLORES[3]} strokeWidth={2} fill="url(#gReb)" />
                </AreaChart>
              </ResponsiveContainer>
              <div className={styles.leyenda}>
                {[['Enviados', COLORES[1]], ['Entregados', COLORES[0]], ['Rebotes', COLORES[3]]].map(([n, c]) => (
                  <span key={n} className={styles.leyendaItem}>
                    <span className={styles.leyendaPunto} style={{ background: c }} />{n}
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.grafica}>
              <div className={styles.graficaTitulo}>Distribución por tipo de correo</div>
              {porTipo.total === 0 ? (
                <p className={styles.graficaVacia}>Sin envíos en el período.</p>
              ) : (
                <div className={styles.anilloLayout}>
                  <div className={styles.anilloWrap}>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={porTipo.filas} dataKey="enviados" nameKey="nombre"
                          cx="50%" cy="50%" innerRadius={58} outerRadius={88}
                          paddingAngle={2} stroke="var(--color-surface)" strokeWidth={2}
                        >
                          {porTipo.filas.map((_, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
                        </Pie>
                        <Tooltip content={<TooltipCorreo />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className={styles.anilloCentro}>
                      <b>{nf.format(porTipo.total)}</b>
                      <span>correos</span>
                    </div>
                  </div>
                  {/* Lista en vez de etiquetas con líneas guía: con tipos del 1%
                      las hojuelas eran invisibles y las guías se cruzaban. */}
                  <ul className={styles.tipoLista}>
                    {porTipo.filas.map((f, i) => (
                      <li key={f.nombre} className={styles.tipoFila}>
                        <span className={styles.tipoPunto} style={{ background: COLORES[i % COLORES.length] }} />
                        <span className={styles.tipoNombre} title={f.codigo}>{f.nombre}</span>
                        <span className={styles.tipoBarra}>
                          <span style={{
                            width: `${pct(f.enviados, porTipo.total)}%`,
                            background: COLORES[i % COLORES.length],
                          }} />
                        </span>
                        <b className={styles.tipoValor}>{nf.format(f.enviados)}</b>
                        <span className={styles.tipoPct}>{pct(f.enviados, porTipo.total)}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {porTipo.sinEnvios.length > 0 && (
                <p className={styles.graficaPie}>
                  <b>Sin envíos en el período:</b>{' '}
                  {porTipo.sinEnvios.map((t) => t.nombre).join(' · ')}
                </p>
              )}
            </div>

            <div className={styles.grafica}>
              <div className={styles.graficaTitulo}>Embudo de conversión</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={embudo} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 4 }}>
                  <defs>
                    {embudo.map((_, i) => (
                      <linearGradient key={i} id={`gEmb${i}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={COLORES[i % COLORES.length]} stopOpacity={0.55} />
                        <stop offset="100%" stopColor={COLORES[i % COLORES.length]} stopOpacity={1} />
                      </linearGradient>
                    ))}
                  </defs>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="etapa" fontSize={12} width={84} tickLine={false} axisLine={false} />
                  <Tooltip content={<TooltipCorreo />} cursor={{ fill: 'var(--color-border-soft)' }} />
                  <Bar dataKey="total" name="Correos" radius={[0, 8, 8, 0]} barSize={26}>
                    {embudo.map((_, i) => <Cell key={i} fill={`url(#gEmb${i})`} />)}
                    <LabelList
                      dataKey="total" position="right" fontSize={12}
                      formatter={(v: unknown) => nf.format(Number(v ?? 0))}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className={styles.graficaPie}>
                Cada etapa sobre lo enviado: {embudo.map((e) => `${e.etapa} ${e.porcentaje}%`).join(' · ')}
              </p>
            </div>

            <div className={styles.grafica}>
              <div className={styles.graficaTitulo}>Envíos por aplicación de origen</div>
              {porApp.length === 0 ? (
                <p className={styles.graficaVacia}>Sin envíos en el período.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={porApp} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gApp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORES[0]} stopOpacity={1} />
                        <stop offset="100%" stopColor={COLORES[0]} stopOpacity={0.45} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--color-border-soft)" vertical={false} />
                    <XAxis dataKey="nombre" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={44} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload as { nombre: string; tecnico: string; remitente: string; enviados: number };
                        return (
                          <div className={styles.tooltip}>
                            <div className={styles.tooltipTitulo}>{d.nombre}</div>
                            <div className={styles.tooltipFila}>
                              <span className={styles.tooltipNombre}>Enviados</span>
                              <b className={styles.tooltipValor}>{nf.format(d.enviados)}</b>
                            </div>
                            {/* El nombre técnico permite rastrear el origen sin
                                obligar a memorizar a qué app corresponde. */}
                            <div className={styles.tooltipPie}>{d.remitente} · {d.tecnico}</div>
                          </div>
                        );
                      }}
                      cursor={{ fill: 'var(--color-border-soft)' }}
                    />
                    <Bar dataKey="enviados" name="Enviados" fill="url(#gApp)" radius={[8, 8, 0, 0]} maxBarSize={64}>
                      <LabelList dataKey="enviados" position="top" fontSize={11} formatter={(v: unknown) => nf.format(Number(v ?? 0))} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ═══ Actividad por hora ═══════════════════════════════════════════
              La hora la reconstruye el backend desde el historial: el NOTIF-API
              solo agrupa por día, aplicación y tipo. */}
          {horas && (
            <section className={styles.seccionHoras}>
              <div className={styles.seccionHead}>
                <span className={styles.seccionIcono}><Clock size={18} /></span>
                <div>
                  <h2>Actividad por hora</h2>
                  <p>
                    A qué hora del día sale el correo — hora de Panamá, acumulando todo el período.
                    {datos.horaTruncado && ` Muestra los ${nf.format(datos.horaAnalizados)} más recientes de ${nf.format(datos.horaTotal)}.`}
                  </p>
                </div>
              </div>

              {!hayActividadHoraria ? (
                <p className={styles.graficaVacia}>
                  <Inbox size={16} /> Sin correos en el período seleccionado.
                </p>
              ) : (
                <>
                  <div className={styles.grafica}>
                    <div className={styles.graficaTitulo}>Volumen por hora</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={serieHoraBarras} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gHora" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLORES[1]} stopOpacity={1} />
                            <stop offset="100%" stopColor={COLORES[1]} stopOpacity={0.4} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="var(--color-border-soft)" vertical={false} />
                        <XAxis dataKey="hora" fontSize={10} tickLine={false} axisLine={false} interval={0} />
                        <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} width={44} />
                        <Tooltip content={<TooltipCorreo sufijo=":00" />} cursor={{ fill: 'var(--color-border-soft)' }} />
                        <Bar dataKey="enviados" name="Enviados" fill="url(#gHora)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className={styles.grafica}>
                    <div className={styles.graficaTitulo}>Por tipo de correo y hora</div>
                    <MapaCalor columnas={horasColumnas} filas={filasTipoHora} />
                  </div>

                  <div className={styles.grafica}>
                    <div className={styles.graficaTitulo}>Enviados, entregados y reportes por hora</div>
                    <MapaCalor columnas={horasColumnas} filas={filasEstadoHora} />
                    <p className={styles.graficaPie}>
                      El desenlace se atribuye a la hora en que salió el correo. Las aperturas no
                      aparecen aquí: el servicio no expone su hora, solo el total del período.
                    </p>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
